import _extends from "@babel/runtime/helpers/extends";
import Button from "@material-ui/core/esm/Button";
import ButtonGroup from "@material-ui/core/esm/ButtonGroup";
import IconButton from "@material-ui/core/esm/IconButton";
import List from "@material-ui/core/esm/List";
import ListItem from "@material-ui/core/esm/ListItem";
import ListItemText from "@material-ui/core/esm/ListItemText";
import TextField from "@material-ui/core/esm/TextField";
import RemoveCircleOutlineTwoToneIcon from '@material-ui/icons/RemoveCircleOutlineTwoTone';
import Autocomplete from "@material-ui/lab/esm/Autocomplete";
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValueLoadable } from 'recoil';
import { EnumCsvAndPdf } from 'api';
import getAgreementsAndVehicle from 'api/agreements/getAgreementsAndVehicle';
import { SecondaryButton } from 'components/Button';
import DatePicker from 'components/DatePicker/DatePicker';
import DialogComponent from 'components/Dialog/Dialog';
import { NOT_AVAILABLE } from 'constants/text';
import { invoiceDownloadUrl } from 'constants/urls';
import dateTimeFormatter from 'utils/date/dateTimeFormatter';
import downloadBlob from 'utils/download/downloadBlob';
import isNorwegian from 'utils/language/isNorwegian';
import checkLoadable from 'utils/recoil/checkLoadable';
import useStyles from './InvoiceReportDownloadableDialogStyles';
export default function InvoiceDownloadDialog({
  open,
  onClose,
  customerId
}) {
  const {
    t,
    i18n
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState(EnumCsvAndPdf.pdf);
  const {
    paramButtonWrapper,
    listContainer,
    optionalButtonLayoutDialog,
    chosenFormat,
    downloadOptionContainer
  } = useStyles();
  const [dates, setDates] = useState({});
  const agreementsData = useRecoilValueLoadable(getAgreementsAndVehicle(customerId));
  const {
    contents,
    loading
  } = checkLoadable(agreementsData);
  const [selectedAgreements, setSelectedAgreements] = useState([]);
  const [selectableAgreements, setSelectableAgreements] = useState([]);
  const mapResultToSelectOptionsWithAgreementId = agreementList => agreementList?.map(({
    name,
    agreementNumber,
    id: value
  }) => ({
    label: `${name} (${agreementNumber})`,
    value
  })) ?? [{
    label: t('error.no_agreements'),
    value: NOT_AVAILABLE
  }];
  const agreements = mapResultToSelectOptionsWithAgreementId(contents);
  useEffect(() => {
    if (!loading && contents) {
      setSelectableAgreements(agreements);
    }
  }, [contents]);
  const resetValues = () => {
    setSelectedAgreements([]);
    setSelectableAgreements(agreements);
  };
  const handleSelectAgreement = value => {
    if (value !== null) {
      setSelectedAgreements([...selectedAgreements, value]);
      setSelectableAgreements(selectableAgreements.filter(item => item !== value));
    }
  };
  const handleDeselect = value => {
    setSelectedAgreements(selectedAgreements.filter(item => item !== value));
    setSelectableAgreements([...selectableAgreements, value]);
  };
  const handleDateChange = input => {
    setDates(props => ({
      ...props,
      dateFrom: input.startDate?.toDate(),
      dateTo: input.endDate?.toDate()
    }));
  };
  const filterOptions = {
    dateFrom: dates.dateFrom,
    dateTo: dates.dateTo,
    language: isNorwegian(i18n) ? 'No' : 'Eng',
    agreementIds: selectableAgreements.length !== 0 ? selectedAgreements.map(record => {
      return record?.value;
    }) : []
  };
  const getInvoiceDownloadData = () => {
    const data = filterOptions;
    const url = invoiceDownloadUrl(customerId);
    const filename = `customer-invoices-report-${dateTimeFormatter(new Date())}.${downloadFormat}`;
    return {
      url,
      filename,
      data
    };
  };
  const startDownloadInvoices = async format => {
    try {
      const downloadArgs = getInvoiceDownloadData();
      await downloadBlob(downloadArgs.url, downloadArgs.filename, 'POST', {
        ...downloadArgs.data,
        format
      });
    } catch {
      enqueueSnackbar(t('error.download_archived_passings'), {
        variant: 'error'
      });
    }
  };
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    await startDownloadInvoices(downloadFormat);
    setIsDownloading(false);
  };
  return /*#__PURE__*/React.createElement(DialogComponent, {
    open: open,
    onClose: () => {
      resetValues();
      onClose();
    },
    dialogTitle: t('invoices.invoice_download.dialog_title'),
    primaryBtnTitle: t('invoices.invoice_download.download'),
    onPrimaryBtnClick: handleDownloadPdf,
    isLoading: isDownloading,
    primaryBtnClickIsDisabled: selectedAgreements.length === 0,
    optionalClass: optionalButtonLayoutDialog,
    optionalsubmitButton: true,
    optionalActionButton: /*#__PURE__*/React.createElement("div", {
      className: paramButtonWrapper
    }, /*#__PURE__*/React.createElement(SecondaryButton, {
      ariaLabel: t('invoices.invoice_download.remove_selected_agreements'),
      onClick: resetValues
    }, t('invoices.invoice_download.remove_selected_agreements')), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10
      }
    }, /*#__PURE__*/React.createElement(SecondaryButton, {
      ariaLabel: t('invoices.invoice_download.select_all_agreements'),
      onClick: () => {
        setSelectedAgreements(agreements);
        setSelectableAgreements([]);
      }
    }, t('invoices.invoice_download.select_all_agreements'))))
  }, /*#__PURE__*/React.createElement("div", {
    className: downloadOptionContainer
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(DatePicker, {
    customClass: true,
    onChange: handleDateChange,
    useUtc: true
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      margin: 20
    }
  }, /*#__PURE__*/React.createElement(ButtonGroup, {
    size: "small",
    color: "primary",
    "aria-labelledby": "choose format for generated report"
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDownloadFormat(EnumCsvAndPdf.csv),
    "aria-labelledby": "CSV format",
    className: clsx({
      [chosenFormat]: downloadFormat === EnumCsvAndPdf.csv
    })
  }, t('download_format.csv')), /*#__PURE__*/React.createElement(Button, {
    onClick: () => setDownloadFormat(EnumCsvAndPdf.pdf),
    "aria-labelledby": "PDF format",
    className: clsx({
      [chosenFormat]: downloadFormat === EnumCsvAndPdf.pdf
    })
  }, t('download_format.pdf'))))), /*#__PURE__*/React.createElement(Autocomplete, {
    id: "Agreement-selector",
    options: selectableAgreements,
    autoHighlight: true,
    disabled: loading,
    getOptionLabel: option => option !== null && Object.values(option)[0] || '',
    getOptionSelected: (option, value) => option?.id === value?.id,
    onChange: (e, agreement) => handleSelectAgreement(agreement),
    renderInput: params => /*#__PURE__*/React.createElement(TextField, _extends({}, params, {
      label: t('invoices.invoice_download.select_agreements')
    }))
  }), /*#__PURE__*/React.createElement(List, {
    className: listContainer
  }, selectedAgreements.map(record => {
    const listLabel = record?.label;
    const agreementId = record?.value;
    return /*#__PURE__*/React.createElement(ListItem, {
      key: agreementId,
      style: {
        display: 'flex',
        flexDirection: 'row-reverse'
      }
    }, /*#__PURE__*/React.createElement(IconButton, {
      edge: "end",
      "aria-label": "comments",
      onClick: () => {
        handleDeselect(record);
      },
      style: {
        justifyContent: 'flex-end'
      }
    }, /*#__PURE__*/React.createElement(RemoveCircleOutlineTwoToneIcon, null)), /*#__PURE__*/React.createElement(ListItemText, {
      id: listLabel,
      primary: listLabel
    }));
  })));
}